import { useState, useEffect, useContext } from "react";
import { useAuth, API, GoogleMapsContext } from "../App";
import { useMyTripsStore } from "../store";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { format, parseISO, isValid } from "date-fns";
import { TripMap } from "../components/TripMap";
import { PackingList } from "../components/PackingList";
import { NearbyEssentials } from "../components/NearbyEssentials";
import { getTripCoverPhoto } from "../utils/countryPhotos";
import { EmptyTrips } from "../components/EmptyStates";

const safeFormatDate = (dateStr, fmt) => {
  if (!dateStr) return "—";
  try {
    const d = parseISO(dateStr);
    return isValid(d) ? format(d, fmt) : "—";
  } catch { return "—"; }
};
import {
  Plus,
  CalendarIcon,
  MapPin,
  Edit2,
  Trash2,
  Compass,
  Loader2,
  Route,
  Camera,
  Mountain,
  Building2,
  Landmark,
  Trees,
  ChevronDown,
  ChevronUp,
  Users,
  UserPlus,
  Mail,
  X,
  Eye,
  Pencil,
  Crown,
  LogOut as LeaveIcon,
  Share2,
  Check,
  Package,
  Navigation,
  Sparkles,
  FileText,
} from "lucide-react";

const MyTrips = () => {
  const { user } = useAuth();
  const isGoogleLoaded = useContext(GoogleMapsContext);
  
  // Use Zustand store for trips
  const { 
    trips, 
    setTrips, 
    addTrip: storeAddTrip,
    updateTrip: storeUpdateTrip,
    removeTrip: storeRemoveTrip,
  } = useMyTripsStore();
  
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedTrips, setExpandedTrips] = useState({});

  // Collaboration state
  const [sharedTrips, setSharedTrips] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteTripId, setInviteTripId] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [collaborators, setCollaborators] = useState({});
  const [expandedCollabs, setExpandedCollabs] = useState({});
  const [showShared, setShowShared] = useState(false);
  const [copiedShareId, setCopiedShareId] = useState(null);

  const handleShareTrip = async (tripId) => {
    try {
      const { data } = await axios.post(`${API}/trips/${tripId}/share`, {}, { withCredentials: true });
      const shareUrl = `${window.location.origin}/api/trip-card/${data.share_id}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopiedShareId(tripId);
      toast.success("Share link copied to clipboard!");
      setTimeout(() => setCopiedShareId(null), 2000);
    } catch {
      toast.error("Failed to generate share link");
    }
  };

  const toggleTripExpand = (tripId) => {
    setExpandedTrips(prev => ({
      ...prev,
      [tripId]: !prev[tripId]
    }));
  };

  // Category icon mapping for sights
  const getCategoryIcon = (category) => {
    const icons = {
      viewpoint: Camera,
      nature: Trees,
      landmark: Landmark,
      city: Building2,
      mountain: Mountain,
    };
    return icons[category] || MapPin;
  };

  const [formData, setFormData] = useState({
    name: "",
    destination_country: "",
    start_date: null,
    end_date: null,
    notes: "",
  });

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const response = await axios.get(`${API}/trips`, {
        withCredentials: true,
      });
      setTrips(response.data);
    } catch (error) {
      toast.error("Failed to load trips");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrip = async () => {
    if (!formData.name || !formData.destination_country || !formData.start_date || !formData.end_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        start_date: format(formData.start_date, "yyyy-MM-dd"),
        end_date: format(formData.end_date, "yyyy-MM-dd"),
      };

      await axios.post(`${API}/trips`, payload, {
        withCredentials: true,
      });

      toast.success("Trip created successfully!");
      setShowCreateModal(false);
      resetForm();
      fetchTrips();
    } catch (error) {
      toast.error("Failed to create trip");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTrip = async () => {
    if (!selectedTrip) return;

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? format(formData.start_date, "yyyy-MM-dd") : undefined,
        end_date: formData.end_date ? format(formData.end_date, "yyyy-MM-dd") : undefined,
      };

      await axios.put(`${API}/trips/${selectedTrip.trip_id}`, payload, {
        withCredentials: true,
      });

      toast.success("Trip updated successfully!");
      setShowEditModal(false);
      resetForm();
      fetchTrips();
    } catch (error) {
      toast.error("Failed to update trip");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTrip = async () => {
    if (!selectedTrip) return;

    try {
      await axios.delete(`${API}/trips/${selectedTrip.trip_id}`, {
        withCredentials: true,
      });

      toast.success("Trip deleted successfully!");
      setShowDeleteDialog(false);
      setSelectedTrip(null);
      fetchTrips();
    } catch (error) {
      toast.error("Failed to delete trip");
    }
  };

  const openEditModal = (trip) => {
    setSelectedTrip(trip);
    setFormData({
      name: trip.name,
      destination_country: trip.destination_country,
      start_date: trip.start_date ? parseISO(trip.start_date) : null,
      end_date: trip.end_date ? parseISO(trip.end_date) : null,
      notes: trip.notes || "",
    });
    setShowEditModal(true);
  };

  const openDeleteDialog = (trip) => {
    setSelectedTrip(trip);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      destination_country: "",
      start_date: null,
      end_date: null,
      notes: "",
    });
    setSelectedTrip(null);
  };

  // ── Collaboration functions ──
  const fetchSharedTrips = async () => {
    try {
      const response = await axios.get(`${API}/trips/shared/list`, { withCredentials: true });
      setSharedTrips(response.data);
    } catch (error) {
    }
  };

  const fetchCollaborators = async (tripId) => {
    try {
      const response = await axios.get(`${API}/trips/${tripId}/collaborators`, { withCredentials: true });
      setCollaborators(prev => ({ ...prev, [tripId]: response.data }));
    } catch (error) {
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail || !inviteTripId) {
      toast.error("Please enter an email address");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/trips/${inviteTripId}/collaborators`, {
        email: inviteEmail,
        role: inviteRole,
      }, { withCredentials: true });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail("");
      setInviteRole("editor");
      fetchCollaborators(inviteTripId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveCollaborator = async (tripId, collabId) => {
    try {
      await axios.delete(`${API}/trips/${tripId}/collaborators/${collabId}`, { withCredentials: true });
      toast.success("Collaborator removed");
      fetchCollaborators(tripId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove collaborator");
    }
  };

  const handleUpdateRole = async (tripId, collabId, newRole) => {
    try {
      await axios.put(`${API}/trips/${tripId}/collaborators/${collabId}`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated");
      fetchCollaborators(tripId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleLeaveTrip = async (tripId, collabId) => {
    try {
      await axios.delete(`${API}/trips/${tripId}/collaborators/${collabId}`, { withCredentials: true });
      toast.success("You left the trip");
      fetchSharedTrips();
    } catch (error) {
      toast.error("Failed to leave trip");
    }
  };

  const toggleCollabPanel = (tripId) => {
    const isExpanding = !expandedCollabs[tripId];
    setExpandedCollabs(prev => ({ ...prev, [tripId]: isExpanding }));
    if (isExpanding && !collaborators[tripId]) {
      fetchCollaborators(tripId);
    }
  };

  useEffect(() => {
    fetchSharedTrips();
  }, []);

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container p-4 pt-6 pb-28" data-testid="my-trips">
      <SEO title="My Trips" description="Manage your trips, view interactive maps, collaborate with friends, and organize packing lists." path="/trips" />
      <div className="max-w-4xl mx-auto page-content">
      {/* Hero Banner */}
      <div className="relative h-36 sm:h-44 overflow-hidden -mx-4 -mt-6 mb-6 rounded-b-2xl">
        <img src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 pb-5 flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold font-heading text-white drop-shadow-lg">My Trips</h1>
            <p className="text-sm text-slate-300/80 mt-0.5 drop-shadow">{trips.length} {trips.length === 1 ? 'trip' : 'trips'} planned</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="btn-primary text-sm"
            data-testid="create-trip-button"
          >
            <Plus className="w-4 h-4 mr-1" />
            New Trip
          </Button>
        </div>
      </div>

      {/* Trip Map */}
      {trips.length > 0 && <TripMap trips={trips} isGoogleLoaded={isGoogleLoaded} />}

      {trips.length === 0 ? (
        <EmptyTrips onAction={() => setShowCreateModal(true)} />
      ) : (
        <div className="space-y-6">
          {trips.map((trip) => (
            <div
              key={trip.trip_id}
              className="trip-card overflow-hidden"
              data-testid={`trip-card-${trip.trip_id}`}
            >
              {/* Cover Photo */}
              <div className="relative -mx-5 -mt-5 mb-4 h-36 sm:h-44 overflow-hidden">
                <img
                  src={getTripCoverPhoto(trip.destination_country)}
                  alt={trip.destination_country || trip.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/80 via-[#0f172a]/20 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4">
                  <h3 className="text-xl font-bold text-white drop-shadow-lg font-heading">{trip.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin className="w-3.5 h-3.5 text-cyan-400" />
                    <span className="text-sm text-slate-300 drop-shadow">{trip.destination_country}</span>
                  </div>
                </div>
                <div className="absolute top-3 right-3 flex gap-1">
                  <button onClick={() => handleShareTrip(trip.trip_id)} className="p-1.5 bg-black/40 backdrop-blur-sm hover:bg-emerald-500/30 rounded-lg transition-colors" data-testid={`social-share-trip-${trip.trip_id}`} title="Copy share link">
                    {copiedShareId === trip.trip_id ? <Check className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4 text-white/80" />}
                  </button>
                  <button onClick={() => openEditModal(trip)} className="p-1.5 bg-black/40 backdrop-blur-sm hover:bg-white/20 rounded-lg transition-colors" data-testid={`edit-trip-${trip.trip_id}`}>
                    <Edit2 className="w-4 h-4 text-white/80" />
                  </button>
                  <button onClick={() => openDeleteDialog(trip)} className="p-1.5 bg-black/40 backdrop-blur-sm hover:bg-red-500/30 rounded-lg transition-colors" data-testid={`delete-trip-${trip.trip_id}`}>
                    <Trash2 className="w-4 h-4 text-red-400/80" />
                  </button>
                </div>
              </div>

              {/* Info Boxes Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {/* Dates Box */}
                <div className="p-3 rounded-xl border border-white/8 bg-white/[0.03]" data-testid={`trip-dates-${trip.trip_id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <CalendarIcon className="w-4 h-4 text-violet-400" />
                    <span className="text-xs font-medium text-violet-300">Dates</span>
                  </div>
                  <p className="text-sm text-white font-medium">
                    {safeFormatDate(trip.start_date, "MMM d")} - {safeFormatDate(trip.end_date, "MMM d, yyyy")}
                  </p>
                </div>

                {/* Route Box */}
                {trip.roadtrip_data?.destinations?.length > 0 && (
                  <div className="p-3 rounded-xl border border-sky-500/20 bg-sky-500/[0.05]" data-testid={`trip-route-${trip.trip_id}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Route className="w-4 h-4 text-sky-400" />
                      <span className="text-xs font-medium text-sky-300">Route</span>
                    </div>
                    <p className="text-sm text-white font-medium">
                      {trip.roadtrip_data.destinations.map(d => d.city).join(" → ")}
                    </p>
                  </div>
                )}

                {/* Description Box */}
                {trip.notes && (
                  <div className={`p-3 rounded-xl border border-white/8 bg-white/[0.03] ${!trip.roadtrip_data?.destinations?.length ? '' : 'md:col-span-2'}`} data-testid={`trip-notes-${trip.trip_id}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <FileText className="w-4 h-4 text-amber-400" />
                      <span className="text-xs font-medium text-amber-300">Description</span>
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed">{trip.notes}</p>
                  </div>
                )}
              </div>

              {/* AI Sights Box */}
              {trip.roadtrip_data?.sights?.length > 0 && (
                <div className="mb-4 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] overflow-hidden" data-testid={`trip-sights-${trip.trip_id}`}>
                  <button
                    onClick={() => toggleTripExpand(trip.trip_id)}
                    className="w-full flex items-center justify-between p-3 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs font-medium text-emerald-300">{trip.roadtrip_data.sights.length} AI-Planned Sights</span>
                    </div>
                    {expandedTrips[trip.trip_id] ? <ChevronUp className="w-4 h-4 text-emerald-400" /> : <ChevronDown className="w-4 h-4 text-emerald-400" />}
                  </button>
                  {expandedTrips[trip.trip_id] && (
                    <div className="px-3 pb-3 space-y-2">
                      {trip.roadtrip_data.sights.map((sight, idx) => {
                        const IconComponent = getCategoryIcon(sight.category);
                        return (
                          <div key={idx} className="flex items-start gap-2.5 p-2.5 bg-white/[0.04] rounded-lg">
                            <IconComponent className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white">{sight.name}</p>
                              {sight.location && <p className="text-xs text-zinc-500 mt-0.5">{sight.location}</p>}
                              {sight.description && <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{sight.description}</p>}
                              <div className="flex flex-wrap gap-2 mt-1">
                                {sight.opening_hours && <span className="text-xs text-sky-400">{sight.opening_hours}</span>}
                                {sight.ticket_price && <span className="text-xs text-emerald-400">{sight.ticket_price}</span>}
                              </div>
                              {sight.website && (
                                <a href={sight.website} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 hover:underline mt-1 inline-block">Visit website</a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Feature Boxes Grid: Collaborators, Packing, Essentials */}
              <div className="grid grid-cols-3 gap-3">
                {/* Collaborators Box */}
                <button
                  onClick={() => toggleCollabPanel(trip.trip_id)}
                  className={`p-3 rounded-xl border text-left transition-all ${expandedCollabs[trip.trip_id] ? 'border-sky-500/30 bg-sky-500/10' : 'border-white/8 bg-white/[0.03] hover:border-sky-500/20 hover:bg-sky-500/[0.05]'}`}
                  data-testid={`toggle-collabs-${trip.trip_id}`}
                >
                  <Users className="w-5 h-5 text-sky-400 mb-1.5" />
                  <span className="text-xs font-medium text-sky-300 block">Collaborators</span>
                </button>

                {/* Packing List Box */}
                <button
                  onClick={() => toggleTripExpand(`pack_${trip.trip_id}`)}
                  className={`p-3 rounded-xl border text-left transition-all ${expandedTrips[`pack_${trip.trip_id}`] ? 'border-amber-500/30 bg-amber-500/10' : 'border-white/8 bg-white/[0.03] hover:border-amber-500/20 hover:bg-amber-500/[0.05]'}`}
                  data-testid={`toggle-packing-${trip.trip_id}`}
                >
                  <Package className="w-5 h-5 text-amber-400 mb-1.5" />
                  <span className="text-xs font-medium text-amber-300 block">Packing List</span>
                </button>

                {/* Nearby Essentials Box */}
                <button
                  onClick={() => toggleTripExpand(`ess_${trip.trip_id}`)}
                  className={`p-3 rounded-xl border text-left transition-all ${expandedTrips[`ess_${trip.trip_id}`] ? 'border-rose-500/30 bg-rose-500/10' : 'border-white/8 bg-white/[0.03] hover:border-rose-500/20 hover:bg-rose-500/[0.05]'}`}
                  data-testid={`toggle-essentials-${trip.trip_id}`}
                >
                  <Navigation className="w-5 h-5 text-rose-400 mb-1.5" />
                  <span className="text-xs font-medium text-rose-300 block">Nearby Essentials</span>
                </button>
              </div>

              {/* Expanded: Collaborators Panel */}
              {expandedCollabs[trip.trip_id] && collaborators[trip.trip_id] && (
                <div className="mt-3 p-3 rounded-xl bg-white/[0.03] border border-sky-500/15 space-y-2" data-testid={`collabs-panel-${trip.trip_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-sky-300">Team</span>
                    <button onClick={() => { setInviteTripId(trip.trip_id); setShowInviteModal(true); }} className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
                      <UserPlus className="w-3 h-3" /> Invite
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Crown className="w-4 h-4 text-amber-400" />
                    <span className="text-white">{collaborators[trip.trip_id].owner?.name || "Owner"}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Owner</span>
                  </div>
                  {collaborators[trip.trip_id].collaborators?.map(c => (
                    <div key={c.collab_id} className="flex items-center justify-between gap-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        {c.role === "editor" ? <Pencil className="w-3 h-3 text-sky-400 flex-shrink-0" /> : <Eye className="w-3 h-3 text-zinc-400 flex-shrink-0" />}
                        <span className="text-white truncate">{c.name || c.email}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${c.role === "editor" ? "bg-sky-500/20 text-sky-400" : "bg-zinc-500/20 text-zinc-400"}`}>{c.role}</span>
                        {c.status === "pending" && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 flex-shrink-0">pending</span>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <select value={c.role} onChange={(e) => handleUpdateRole(trip.trip_id, c.collab_id, e.target.value)} className="text-xs bg-white/5 border border-white/10 rounded px-1 py-0.5 text-zinc-300" data-testid={`role-select-${c.collab_id}`}>
                          <option value="editor">Editor</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <button onClick={() => handleRemoveCollaborator(trip.trip_id, c.collab_id)} className="p-1 hover:bg-red-500/10 rounded transition-colors" data-testid={`remove-collab-${c.collab_id}`}>
                          <X className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {(!collaborators[trip.trip_id].collaborators || collaborators[trip.trip_id].collaborators.length === 0) && (
                    <p className="text-xs text-zinc-500">No collaborators yet.</p>
                  )}
                </div>
              )}

              {/* Expanded: Packing List */}
              {expandedTrips[`pack_${trip.trip_id}`] && (
                <div className="mt-3">
                  <PackingList tripId={trip.trip_id} destination={trip.destination_country} startDate={trip.start_date} endDate={trip.end_date} />
                </div>
              )}

              {/* Expanded: Nearby Essentials */}
              {expandedTrips[`ess_${trip.trip_id}`] && trip.destination_country && (
                <div className="mt-3">
                  <NearbyEssentials countryCode={trip.destination_country} compact={true} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Shared with Me Section */}
      {sharedTrips.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setShowShared(!showShared)}
            className="flex items-center gap-2 mb-4"
            data-testid="toggle-shared-trips"
          >
            <Users className="w-5 h-5 text-sky-400" />
            <h2 className="text-lg font-semibold text-white">Shared with Me ({sharedTrips.length})</h2>
            {showShared ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
          </button>
          {showShared && (
            <div className="space-y-4">
              {sharedTrips.map((trip) => (
                <div
                  key={trip.trip_id}
                  className="trip-card border-l-4 border-sky-500/50"
                  data-testid={`shared-trip-${trip.trip_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white">{trip.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${trip.collaboration_role === "editor" ? "bg-sky-500/20 text-sky-400" : "bg-zinc-500/20 text-zinc-400"}`}>
                          {trip.collaboration_role}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-zinc-400">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{trip.destination_country}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-zinc-500">
                        <CalendarIcon className="w-4 h-4" />
                        <span className="text-sm">
                          {safeFormatDate(trip.start_date, "MMM d")} - {safeFormatDate(trip.end_date, "MMM d, yyyy")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Invite Collaborator Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="glass-card border-white/10" data-testid="invite-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-sky-400" />
              Invite Collaborator
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Email Address</Label>
              <Input
                type="email"
                placeholder="friend@example.com"
                className="glass-input"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="glass-input" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-card border-white/10">
                  <SelectItem value="editor">
                    <div className="flex items-center gap-2">
                      <Pencil className="w-3 h-3" />
                      <span>Editor — can edit trip details</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <div className="flex items-center gap-2">
                      <Eye className="w-3 h-3" />
                      <span>Viewer — read-only access</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-sky-500/10 border border-sky-500/20 rounded-lg">
              <p className="text-xs text-sky-300">
                An invitation email will be sent. If the user has an Explorio account, they'll get instant access.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)} className="btn-outline">
              Cancel
            </Button>
            <Button onClick={handleInvite} className="btn-primary" disabled={isSubmitting} data-testid="send-invite-button">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Modal */}
      <Dialog
        open={showCreateModal || showEditModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateModal(false);
            setShowEditModal(false);
            resetForm();
          }
        }}
      >
        <DialogContent className="glass-card border-white/10" data-testid="trip-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {showEditModal ? "Edit Trip" : "Create New Trip"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Trip Name</Label>
              <Input
                placeholder="e.g., Summer in Japan"
                className="glass-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="trip-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Destination Country</Label>
              <Input
                placeholder="e.g., Japan"
                className="glass-input"
                value={formData.destination_country}
                onChange={(e) =>
                  setFormData({ ...formData, destination_country: e.target.value })
                }
                data-testid="trip-destination-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal glass-input overflow-hidden"
                      data-testid="trip-start-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span className="truncate">
                        {formData.start_date
                          ? format(formData.start_date, "MMM d, yyyy")
                          : "Pick a date"}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 glass-card border-white/10">
                    <Calendar
                      mode="single"
                      selected={formData.start_date}
                      onSelect={(date) => setFormData({ ...formData, start_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal glass-input overflow-hidden"
                      data-testid="trip-end-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span className="truncate">
                        {formData.end_date
                          ? format(formData.end_date, "MMM d, yyyy")
                          : "Pick a date"}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 glass-card border-white/10">
                    <Calendar
                      mode="single"
                      selected={formData.end_date}
                      onSelect={(date) => setFormData({ ...formData, end_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Notes (Optional)</Label>
              <Textarea
                placeholder="Add any notes about your trip..."
                className="glass-input min-h-[100px]"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                data-testid="trip-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                resetForm();
              }}
              className="btn-outline"
            >
              Cancel
            </Button>
            <Button
              onClick={showEditModal ? handleUpdateTrip : handleCreateTrip}
              className="btn-primary"
              disabled={isSubmitting}
              data-testid="trip-submit-button"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : showEditModal ? (
                "Update Trip"
              ) : (
                "Create Trip"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="glass-card border-white/10" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Trip</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete "{selectedTrip?.name}"? This action cannot
              be undone and will also delete all associated tickets and bookings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-outline">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTrip}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-trip"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};

export default MyTrips;
